<template>
  <v-row class="pa-0 mx-0 justify-center">
    <v-col class="rep pa-4 mx-12 justify-center" cols="12" v-if="hasReps">
      <!-- Your Representative, George Livingston, can help. -->
      <v-row
        align="center"
        :class="$vuetify.display.xs ? 'justify-center' : 'justify-center'"
        class="mb-2"
      >
        <v-col cols="auto" class="d-flex">
          <v-img
            v-if="agentImageUrl"
            alt="Rep"
            height="60px"
            width="80px"
            :src="agentImageUrl || repFallbackImage"
            class="mx-0 px-0"
          >
            <!-- Content to display if the main src fails to load -->
            <template v-slot:error>
              <v-img class="mx-auto" height="60px" width="80px" :src="repFallbackImage"></v-img>
            </template>
          </v-img>
        </v-col>
        <v-col cols="auto" class="d-flex align-center justify-center pb-0">
          <div class="body-1 ml-4 justify-center align-center mx-0 px-0 greyText">
            {{ $t('L_your_representative', { agent: agentName }) }}
          </div>
        </v-col>
      </v-row>
      <v-row v-if="reps && reps.length > 0" class="mt-0 ml-2 mb-4 pb-4 justify-left">
        <v-col cols="12" v-for="rep in reps" :key="rep.agentId">
          <v-row :class="$vuetify.display.xs ? 'text-left' : 'text-left'" class="justify-left">
            <v-col
              cols="12"
              sm="4"
              md="3"
              v-if="rep.office && rep.office.phone"
              class="repContactLinkPLPR pb-0"
            >
              <span class="font-size--14 font-weight-medium pr-2">
                <v-icon color="primary" class="font-size--24" :icon="mdiPhone" />
              </span>
              <a
                :href="'tel:+1' + rep.office.phone"
                class="text-decoration-none font-weight-bold blueText font-size--14"
                >{{ phoneFormat(rep.office.phone) }}</a
              >
            </v-col>
            <v-col
              cols="12"
              sm="4"
              md="3"
              v-if="rep.useCellPhone && rep.cell"
              class="repContactLinkPLPR pb-0"
            >
              <span class="font-size--14 font-weight-medium pr-2">
                <v-icon color="primary" class="font-size--24" :icon="mdiCellphone" />
              </span>
              <a
                :href="'tel:+1' + rep.cell"
                class="text-decoration-none font-weight-bold blueText font-size--14"
                >{{ phoneFormat(rep.cell) }}</a
              >
            </v-col>
            <v-col
              cols="12"
              sm="4"
              md="3"
              v-if="rep.office && rep.office.fax"
              class="repContactLinkPLPR pb-0"
            >
              <span class="font-size--14 font-weight-medium pr-2">
                <v-icon color="primary" class="font-size--24" :icon="mdiPrinter" />
              </span>
              <a
                :href="'tel:+1' + rep.office.fax"
                class="text-decoration-none font-weight-bold blueText font-size--14"
                >{{ phoneFormat(rep.office.fax) }}</a
              >
            </v-col>
            <v-col cols="12" sm="4" md="3" v-if="rep.email" class="repContactLinkPLPR pb-0">
              <span class="font-size--14 font-weight-medium pr-2">
                <v-icon color="primary" class="font-size--24" :icon="mdiEmail" />
              </span>
              <a
                @click.prevent="emailRep(rep.agentId)"
                href="$t('L_rep_email_me')"
                class="text-decoration-none font-weight-bold blueText font-size--14"
                >{{ $t('L_rep_email_me') }}</a
              >
            </v-col>
            <v-col
              cols="12"
              sm="4"
              md="3"
              v-if="rep.appointmentUrl"
              class="repContactLinkPLPR pb-0"
            >
              <span class="font-size--14 font-weight-medium pr-2">
                <v-icon color="primary" class="font-size--24" :icon="mdiCalendar" />
              </span>
              <a
                @click.prevent="openCalendar(rep.appointmentUrl)"
                href="$t('L_make_an_appointment')"
                class="text-decoration-none font-weight-bold blueText font-size--14"
                >{{ $t('L_make_an_appointment') }}</a
              >
            </v-col>
          </v-row>
        </v-col>
        <ContactRep
          v-if="contactAgent"
          v-model="contactMe"
          :agents="[contactAgent]"
          @close="contactMe = false"
          @send-message="send($event)"
          close-selector="contact_your_rep_myrep_close"
          modal-class="contact_your_rep_myrep_modal"
          :sender="sender"
        />
        <MessageDialog
          :show-dialog="dialog"
          :has-button-two="true"
          :is-persistent="true"
          type="info"
          :title="$t('L_you_are_being_redirected_to_an_appointment_scheduling_website')"
          :message="$t('L_this_link_will_take_you_to_a_third_party_site_that_has_its_own')"
          :button-two-label="$t('L_go_back')"
          :button-one-label="$t('L_continue')"
          @buttonTwoAction="dialog = false"
          @buttonOneAction="closeAndGoToCalendar"
        />
      </v-row>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import MessageDialog from '@/components/MessageDialog.vue';
import { openInNewWindow } from '@/mixins/openNewWindow';
import { phoneNumber } from '@/shared/util/formatters';
import ContactRep from '../ContactRep/ContactRep.vue';
import { mapActions, mapState } from 'pinia';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { useGlobalStore } from '@/stores/global/global.store';
import repFallbackImage from '@/assets/rep-thumb.svg';
import { mdiCellphone, mdiCalendar, mdiEmail, mdiPhone, mdiPrinter } from '@mdi/js';
import { defineComponent, type ComponentOptions } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import type { IGlobalStoreAgent } from '@/stores/global/globalStore.model.ts';

const componentOptions: ComponentOptions = {
  name: 'ContactRepFooter',
  components: {
    ContactRep,
    MessageDialog,
  },
  mixins: [openInNewWindow],
  data() {
    return {
      dialog: false,
      // contactRep: false,
      contactMe: false,
      contactAgent: {},
      repFallbackImage,
      mdiPhone,
      mdiCellphone,
      mdiPrinter,
      mdiEmail,
      mdiCalendar,
      calendarLink: '',
      $v: useVuelidate(),
    };
  },
  computed: {
    ...mapState(usePolicyStore, ['currentPolicy', 'policiesSortedByDate']),
    ...mapState(useCustomerStore, ['investmentsSortedByDate', 'customerProfile']),
    ...mapState(useGlobalStore, ['getAgent', 'agents']),
    hasReps() {
      if (!Array.isArray(this.agents)) return false;
      return this.agents?.length > 0;
    },
    agentName() {
      return this.agent?.name ? this.agent.name : '';
    },
    sender() {
      try {
        if (!this.customerProfile || typeof this.customerProfile !== 'object') {
          // Provide safe defaults to prevent undefined errors in ContactRep component
          return {
            email: '',
            phone: '',
            firstName: '',
            lastName: '',
          };
        }
        return {
          email: this.customerProfile?.email?.trim() || '',
          phone: this.customerProfile?.mobilePhone?.trim() || '',
          firstName: this.customerProfile?.firstName?.trim() || '',
          lastName: this.customerProfile?.lastName?.trim() || '',
        };
      } catch (_error) {
        return { email: '', phone: '', firstName: '', lastName: '' };
      }
    },
    agent() {
      return this.agents?.length > 0 ? this.agents[0] : ({} as { [key: string]: never });
    },
    agentImageUrl() {
      return this.agent?.displayPic;
    },
    altAgentImageUrl() {
      return `Rep pic ${this.agent?.displayPic}`;
    },
    reps() {
      try {
        // Validate arrays before iteration
        if (
          !Array.isArray(this.policiesSortedByDate) ||
          !Array.isArray(this.investmentsSortedByDate)
        ) {
          return [];
        }

        const accounts: Array<{ product: string; agentId: string }> = [];
        this.policiesSortedByDate.forEach((pol: { contractId?: string; agentId: string }) => {
          if (pol.contractId && pol.contractId.length > 0) {
            accounts.push({
              product: `${this.$t('L_term_life_policy')} ${pol.contractId}`,
              agentId: pol.agentId,
            });
          }
        });

        this.investmentsSortedByDate.forEach(
          (acct: { accountNumber?: string; productType?: string; agentId: string }) => {
            if (acct.accountNumber && acct.accountNumber.length > 0) {
              accounts.push({
                product: `${acct.productType} ${acct.accountNumber}`,
                agentId: acct.agentId,
              });
            }
          }
        );

        const reps: IGlobalStoreAgent[] = [];

        accounts.forEach((acct) => {
          if (!acct.agentId) return; // Skip if agentId missing

          const found = reps.find((a) => a.agentId === acct.agentId);

          if (!found) {
            const storeAgent = this.getAgent(acct.agentId) as IGlobalStoreAgent | undefined;
            if (storeAgent?.agentId) {
              const agentCopy: IGlobalStoreAgent = {
                ...storeAgent,
                products: [acct.product], // Set on copy, not original
                displayName: storeAgent.name ?? '', // Set on copy, not original
              };
              reps.push(agentCopy);
            }
          } else {
            // Mutate the local copy in reps array only
            if (!Array.isArray(found.products)) {
              found.products = []; // Initialize if missing
            }
            found.products.push(acct.product);
          }
        });
        return reps;
      } catch (_error) {
        return []; // Safe fallback — renders hasReps as false
      }
    },
  },
  methods: {
    ...mapActions(useCustomerStore, ['contactRepresentative']),
    emailRep(agentId: string) {
      const agent = this.reps.find((a: IGlobalStoreAgent) => a.agentId === agentId);
      this.contactAgent = agent;
      this.contactMe = true;
    },
    openCalendar(calendarLink: string) {
      this.calendarLink = calendarLink;
      this.dialog = true;
    },
    async closeAndGoToCalendar() {
      this.dialog = false;
      await this.openInNewWindow({ url: this.calendarLink, target: '_blank' });
    },
    phoneFormat(number: string) {
      return phoneNumber(number);
    },
    send(emailPayload: object) {
      this.contactRepresentative(emailPayload);
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.contactYourRepButtonWidth {
  width: 264px;
  max-width: 265px;
  min-width: 264px;
}

.picture {
  width: 80px;
  .agentImage {
    .icon {
      width: 80px;
      height: 60px;
    }
  }
}

.rep {
  background: #eee;

  .button-wrapper {
    .v-btn {
      background-color: #063562 !important;
    }
  }
}

.border {
  border: 1px solid black;
}

.blueText {
  color: #02579e;
  font-weight: bold;
}

.centerVertically {
  line-height: 60px;
  height: 60px;
  text-align: center;
}

.centerVerticallySpan {
  display: inline-block;
  vertical-align: text-bottom;
}

.displayBlock {
  display: block;
}

.greyText {
  color: #333333 !important;
  font-weight: 600;
}
.repContactLinkPLPR {
  padding-left: 4px;
  padding-right: 4px;
}
</style>
